import { Word } from '@/common/types/scopeAndSequence';
import { ControllerType } from '@/common/types/student';
import {
  playWordDefinition,
  playWordInSentence,
  stopAudio,
} from '@/common/utils/audio';
import { getDistractorGraphemes } from '@/common/utils/helpers';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { getWordById, getWordGraphemes } from '@/common/utils/scopeAndSequence';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import { getStudent } from '@/game/GameComponents/PhaserGame/GameInstance';
import { StudentGameInputHandler } from '@/game/GameComponents/PhaserGame/Input/StudentGameInputHandler';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import { getSceneManager } from '@/game/GameComponents/PhaserGame/Managers/SceneManager';
import { DEFAULT_SCALE_DOWN_CONFIG } from '@/game/GameComponents/PhaserGame/Types/ManagerTypes/ResponsivenessManagerTypes';
import { getWordSetFromLevel } from '@/game/GameComponents/PhaserGame/Utils/duelUtils';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import { HorizontalLayoutGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/HorizontalLayoutGroup';
import {
  getAnchorPlugin,
  isPhaserDevMode,
  waitForMilliseconds,
  waitForSeconds,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import {
  playAIWordWithCards,
  playAIWordWithKeyboard,
} from '@/game/GameComponents/PhaserGame/Utils/StudentGameUtils';
import {
  getHighestLevelForPhase,
  getPhaseFromLevel,
} from '@/game/prefabs/Assessment/assessmentHelpers';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import DuelWordCreationPanel from '@/game/scenes/DuelScenes/DuelWordCreationPanel';
import _ from 'lodash';

export default class DuelWordCreationPanelController {
  constructor(private _wordCreationPanel: DuelWordCreationPanel) {}

  lastWord: Word | null = null;
  defaultWordColour: number = 0x52333f;
  correctWordColour: number = 0x39e667;
  incorrectWordColour: number = 0xe64539;
  autoValidateWords: boolean = false; //used for development just to test game mechanics
  userButtonLayoutGroup?: HorizontalLayoutGroup;
  numIconsToPreload: number = 4;
  currentWordSet: Word[] = [];
  currentTargetWord: Word | null = null;

  get wordCreationPanel() {
    return this._wordCreationPanel;
  }

  initialize() {
    // Create input handler.
    const { _wordCreationPanel: wordCreationPanel } = this;
    wordCreationPanel.inputHandler = new StudentGameInputHandler(
      wordCreationPanel,
    );
    wordCreationPanel.inputHandler.updateCurrentInputSystem(
      wordCreationPanel.getControlScheme(getStudent().settings.controllerType),
    );

    // Create button layout group.
    this.userButtonLayoutGroup = new HorizontalLayoutGroup({
      parent: wordCreationPanel.gameButtonsContainer,
      origin: new Phaser.Math.Vector2(0, 0),
      elementDist: 4,
      align: 'center',
    });

    // Setup the board for the control scheme.
    wordCreationPanel.setupBoardForControlScheme(
      wordCreationPanel.getControlScheme(getStudent().settings.controllerType),
    );

    // Generate the word list.
    this.generateWordList();

    // Preload definition and example images.
    wordCreationPanel.preloadHelperIcons();

    // Button callbacks.
    wordCreationPanel.autoWinButton.setVisible(isPhaserDevMode(getStudent()));
    wordCreationPanel.autoWinButton.setVisible(false);
    this.userButtonLayoutGroup.updateLayout();
    wordCreationPanel.submitButton.controller.setSubmissionCallback(() =>
      this.checkAnswer(),
    );

    wordCreationPanel.repeatInstructionButton.setSubmissionCallback(() => {
      this.repeatCurrentWord();
    });

    wordCreationPanel.playDefinitionButton.setSubmissionCallback(() => {
      this.playWordDefinition();
    });

    wordCreationPanel.playWordInSentenceButton.setSubmissionCallback(() => {
      this.playWordInSentence();
    });

    this.initializeResponsiveness();

    // Start off hidden, will be shown when the duel starts.
    wordCreationPanel.scene.setVisible(false);
    wordCreationPanel.getDefinitionExampleView().setVisible(false);

    // Event listeners.
    EventsManager.registerEventOn(
      wordCreationPanel,
      GAME_EVENTS.PLAY_AI_WORD,
      this.playAIWord,
      this,
    );
    EventsManager.registerEventOn(
      wordCreationPanel,
      GAME_EVENTS.WORD_WAS_PLAYED,
      this.handleWordWasPlayed,
      this,
    );
    EventsManager.registerEventOn(
      wordCreationPanel,
      GAME_EVENTS.SWITCH_TURN,
      this.handleTurnSwitch,
      this,
    );
    EventsManager.registerEventOn(
      wordCreationPanel,
      GAME_EVENTS.INPUT_METHOD_CHANGED,
      wordCreationPanel.setupBoardForControlScheme,
      this,
    );
    EventsManager.registerEventOn(
      wordCreationPanel,
      GAME_EVENTS.RIPOSTE_STARTED,
      this.handlePlayerStartedRiposte,
      this,
    );
    EventsManager.registerEventOn(
      wordCreationPanel,
      GAME_EVENTS.DUELIST_ATTEMPTED_RIPOSTE,
      this.handlePlayerAttemptedRiposte,
      this,
    );
    EventsManager.registerEventOn(
      wordCreationPanel,
      GAME_EVENTS.STUDENT_GAME_KEYBOARD_INPUT,
      this.handleStudentGameKeyboardInput,
      this,
    );
  }

  initializeNewMinigameCard(cardText: string) {
    const { _wordCreationPanel: wordCreationPanel } = this;
    const newCard = new StudentGameCard(wordCreationPanel, 0, 0);
    newCard.setCardText(cardText);
    wordCreationPanel.inputHandler?.initializeCardInput(newCard);
    wordCreationPanel.getHandDropSite().layoutGroup?.pushCard(newCard);
    newCard.origin = {
      dropSite: wordCreationPanel.getHandDropSite(),
      index:
        wordCreationPanel
          .getHandDropSite()
          .layoutGroup?.cards.indexOf(newCard) ?? 0,
    };
  }

  async newRound() {
    const { _wordCreationPanel: wordCreationPanel } = this;
    // Get the next target word.
    let newTargetWord = this.currentWordSet.shift();
    if (!newTargetWord) {
      this.generateWordList();
      newTargetWord = this.currentWordSet.shift();
      if (!newTargetWord) {
        console.error('No target word found!');
        return;
      }
    }
    this.lastWord = this.currentTargetWord;
    this.currentTargetWord = newTargetWord;

    // Lazy load the next icon.
    this.lazyLoadNextIcon();

    // Clear currrent cards.
    wordCreationPanel.clearCards();

    // Clear input text.
    await wordCreationPanel.clearTypedInput();

    // Create the cards for the new word.
    const nextWord = this.currentTargetWord;
    if (!nextWord) return;

    // Shuffle the word.
    const wordGraphemes = getWordGraphemes(nextWord);
    let splitNextWord: string[] = wordGraphemes.map(
      (grapheme) => grapheme.spelling,
    );
    let shuffledNextWord: string[] = [];

    for (let i = 0; i < wordGraphemes.length; i++) {
      const nextGrapheme = _.sample(
        splitNextWord.filter(
          (graphemeSpelling) => graphemeSpelling !== splitNextWord[i],
        ),
      );
      if (nextGrapheme) {
        shuffledNextWord.push(nextGrapheme);
        splitNextWord.splice(splitNextWord.indexOf(nextGrapheme), 1);
      } else {
        shuffledNextWord.push(splitNextWord[i]);
        splitNextWord.splice(i, 1);
      }
    }

    const highestLevelForPhase = getHighestLevelForPhase(
      getPhaseFromLevel(getStudent().level),
    );
    const numDistractorCards = 3;
    const DISTRACTOR_COUNT = 3;
    const distractorLetters = getDistractorGraphemes(
      wordGraphemes,
      highestLevelForPhase,
      DISTRACTOR_COUNT,
    );
    let shuffledWordWithDistractors = [...shuffledNextWord];

    // Insert distractor letters at random positions.
    for (
      let i = 0;
      i < Math.min(numDistractorCards, distractorLetters.length);
      i++
    ) {
      const randomDistractorPos = _.random(0, shuffledNextWord.length);
      shuffledWordWithDistractors.splice(
        randomDistractorPos,
        0,
        distractorLetters[i].spelling,
      );
    }

    for (const letter of shuffledWordWithDistractors) {
      this.initializeNewMinigameCard(letter);
    }

    // Make the UI interactable if it is the player's turn.
    EventBus.emit(GAME_EVENTS.NEW_DUEL_WORD_STARTED);
  }

  verifyAIWord(word: string) {
    const levelWord = this.currentTargetWord?.spelling;
    this.doWordValidationFeedback(word === levelWord, 1);

    const targetWord = getWordById(this.currentTargetWord?.id ?? '');
    if (!targetWord) {
      console.error('No target word found!');
      return;
    }

    // EventBus.emit(GAME_EVENTS.AI_PLAYED_WRONG_WORD, {
    //   wordCreationPanel: this,
    //   cards: this.getAllCards(),
    //   targetWord: this.currentTargetWord,
    // });
  }

  doWordValidationFeedback(isValid: boolean, duelistIndex: number) {
    const { _wordCreationPanel: wordCreationPanel } = this;
    if (isValid) {
      if (
        wordCreationPanel.getControlScheme(
          getStudent().settings.controllerType,
        ) === ControllerType.KEYBOARD
      ) {
        // Keyboard correct feedback.
        wordCreationPanel.getPlayerTypingInputs()[0].doSuccessFeedback();
      } else {
        // Card correct feedback.
        wordCreationPanel.getBoardCards().forEach((card) => {
          card.showCorrectFeedback();
        });
      }
      PhaserAudioManager.playSFX(wordCreationPanel, 'spell-correct');
    } else {
      if (
        wordCreationPanel.getControlScheme(
          getStudent().settings.controllerType,
        ) === ControllerType.KEYBOARD
      ) {
        // Keyboard correct feedback.
        wordCreationPanel.getPlayerTypingInputs()[0].doIncorrectFeedback();
      } else {
        // Card correct feedback.
        wordCreationPanel.getBoardCards().forEach((card) => {
          card.showIncorrectFeedback();
        });
      }
      PhaserAudioManager.playSFX(wordCreationPanel, 'spell-incorrect');
    }

    EventBus.emit(GAME_EVENTS.WORD_WAS_PLAYED, {
      wordCreationPanel: this._wordCreationPanel,
      duelistIndex: duelistIndex,
      wordWasValid: isValid,
      word: this.currentTargetWord,
    });
  }

  async playAIWord() {
    const { _wordCreationPanel: wordCreationPanel } = this;
    wordCreationPanel.setBoardInteractive(false);
    if (!this.currentTargetWord) {
      console.error('No current target word!');
      return;
    }

    const wordGraphemes = getWordGraphemes(this.currentTargetWord);
    const aiSpeedMod = 1;

    await waitForSeconds(wordCreationPanel.time, 1.5);
    switch (
      wordCreationPanel.getControlScheme(getStudent().settings.controllerType)
    ) {
      case ControllerType.KEYBOARD:
        playAIWordWithKeyboard(
          wordCreationPanel,
          wordGraphemes,
          aiSpeedMod,
          (attempt: string) => this.verifyAIWord(attempt),
        );
        break;
      case ControllerType.CLICK:
        playAIWordWithCards(
          wordCreationPanel,
          wordGraphemes,
          aiSpeedMod,
          this.currentTargetWord,
          (attempt: string) => this.verifyAIWord(attempt),
        );
        break;
    }
  }

  checkAnswer() {
    stopAudio();

    const attempt = this._wordCreationPanel.getInput();
    if (!attempt.length) return;

    let wordIsValid = true;
    if (!this.autoValidateWords) {
      wordIsValid = attempt === this.currentTargetWord?.spelling;
    }
    this.doWordValidationFeedback(wordIsValid, 0);
    this._wordCreationPanel.setBoardInteractive(false);
    this._wordCreationPanel.inputHandler?.clearCardGuidance();
  }

  async repeatCurrentWord() {
    if (!this.currentTargetWord) {
      console.error('No current target word!');
      return;
    }
    stopAudio();
    await playWordInSentence(this.currentTargetWord);
  }

  async playWordInSentence() {
    const word = this.currentTargetWord;
    if (!word) {
      console.error('No word found');
      return;
    }
    stopAudio();
    const student = getStudent();
    const studentId = student.id;
    if (studentId) {
      trackMixpanelEvent('Example button clicked', {
        word: word.example,
      });
    }

    this._wordCreationPanel.showExampleForWord(word);
    await playWordInSentence(word);
  }

  async playWordDefinition() {
    const word = this.currentTargetWord;
    if (!word) {
      console.error('No word found');
      return;
    }
    stopAudio();
    const student = getStudent();
    const studentId = student.id;
    if (studentId) {
      trackMixpanelEvent('Definition button clicked', {
        word: word.spelling,
      });
    }

    this._wordCreationPanel.showDefinitionForWord(word);
    await playWordDefinition(word);
  }

  generateWordList() {
    const wordSet = getWordSetFromLevel(getStudent().level);
    this.currentWordSet = _.shuffle(wordSet);
    if (
      this.lastWord &&
      this.lastWord.spelling === this.currentWordSet[0].spelling
    ) {
      // Swap the first and second words.
      this.currentWordSet[0] = this.currentWordSet[1];
      this.currentWordSet[1] = this.lastWord;
    }
  }

  lazyLoadNextIcon() {
    const { _wordCreationPanel: wordCreationPanel } = this;
    const upcomingWord = this.currentWordSet[this.numIconsToPreload - 1];
    if (!upcomingWord) return;

    const definitionIcon = _.kebabCase(upcomingWord.spelling);
    const exampleIcon = _.kebabCase(upcomingWord.example);

    wordCreationPanel.load.image(
      definitionIcon,
      `game_assets/definition_images/${definitionIcon}.png`,
    );
    wordCreationPanel.load.image(
      exampleIcon,
      `game_assets/example_images/${exampleIcon}.png`,
    );
    wordCreationPanel.load.start();
  }

  getDefinitionIconsToLoad() {
    return this.currentWordSet
      .slice(0, this.numIconsToPreload)
      .map((w) => _.kebabCase(w.spelling));
  }

  getExampleIconsToLoad() {
    return this.currentWordSet
      .slice(0, this.numIconsToPreload)
      .map((w) => _.kebabCase(w.example));
  }

  initializeResponsiveness() {
    const { _wordCreationPanel: wordCreationPanel } = this;

    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      wordCreationPanel.mainContainer,
      DEFAULT_SCALE_DOWN_CONFIG,
    );

    ResponsivenessManager.InitializeResizeHandler(
      this._wordCreationPanel,
      () => this.handleResize(),
      this,
    );
    this.handleResize();
  }

  async handleResize() {
    const { _wordCreationPanel: wordCreationPanel } = this;
    await waitForMilliseconds(wordCreationPanel.time, 1);
    if (!wordCreationPanel || !wordCreationPanel.scene.isActive()) return;

    const breakpoint = ResponsivenessManager.GetCurrentGlobalViewForGame(
      this._wordCreationPanel,
    );

    const anchor = getAnchorPlugin(wordCreationPanel);
    if (anchor) {
      if (breakpoint === 'mobile') {
        anchor.add(wordCreationPanel.mainContainer, {
          centerX: '50%',
          bottom: '100%',
        });
      } else {
        anchor.add(wordCreationPanel.mainContainer, {
          centerX: '50%',
          bottom: '100%-32',
        });
      }
    }
  }

  handleTurnSwitch() {
    this.newRound(); // Reinitialize the game board when the turn is switched.
  }

  handleWordWasPlayed() {
    this._wordCreationPanel.setBoardInteractive(false);
  }

  handlePlayerStartedRiposte() {
    getSceneManager()?.pauseScene('DuelWordCreationPanel');
  }

  handlePlayerAttemptedRiposte() {
    const { _wordCreationPanel: wordCreationPanel } = this;
    getSceneManager()?.resumeScene('DuelWordCreationPanel');
    wordCreationPanel.clearCards();
    wordCreationPanel.getPlayerTypingInputs()[0].clearInput();
  }

  handleStudentGameKeyboardInput() {
    const { _wordCreationPanel: wordCreationPanel } = this;
    const definitionExampleImageView =
      wordCreationPanel.getDefinitionExampleView();
    wordCreationPanel.tweens.killTweensOf(definitionExampleImageView);
    definitionExampleImageView.setVisible(false);
  }
}
